import { useState } from "react"
import { useNavigate, useRouter } from "@tanstack/react-router"

import { Shortcut } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { SuggestionProposal } from "./use-article-suggestions"
import { toast } from "@/components/toast"
import { useUndoContext } from "@/context/undo-context"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	createDraftKey,
	useReviewDraftStore,
} from "@/stores/review-draft-store"
import { nextSuggestionRow, reviewRouteForRow } from "./review-navigation"
import { useArticleSuggestions } from "./use-article-suggestions"

interface AcceptStep {
	proposalId: string
	newArticleVersionId: string
	newArticleId?: string
	newArticleUrlName?: string
	discardedHunkIds?: string[]
	editedHtml?: string
	editedTitle?: string
}

type BulkStep =
	| { action: "accept"; args: AcceptStep }
	| { action: "reject"; proposalId: string }

export interface BulkPlanInput {
	proposals: SuggestionProposal[]
	discardedByProposal?: Map<string, string[]>
	hunkCountByProposal?: Map<string, number>
	draft?: { html?: string; title?: string }
	newId: () => string
}

// A proposal whose every hunk the reviewer discarded has nothing left to apply,
// so it is rejected rather than accepted. The reviewer's edited body lands on
// the last non-DELETE accept, since `applyEditProposal` writes it verbatim and
// it already contains every earlier proposal.
export function planBulkAccept({
	proposals,
	discardedByProposal,
	hunkCountByProposal,
	draft,
	newId,
}: BulkPlanInput): BulkStep[] {
	const applicable = proposals.filter((p) => {
		const discarded = discardedByProposal?.get(p.id)?.length ?? 0
		const total = hunkCountByProposal?.get(p.id) ?? 0
		return !(total > 0 && discarded >= total)
	})
	const lastEditable = applicable.filter((p) => p.kind !== "DELETE").at(-1)

	return proposals.map((proposal): BulkStep => {
		if (!applicable.includes(proposal)) {
			return { action: "reject", proposalId: proposal.id }
		}
		const discarded = discardedByProposal?.get(proposal.id) ?? []
		const carriesDraft = proposal === lastEditable
		return {
			action: "accept",
			args: {
				proposalId: proposal.id,
				newArticleVersionId: newId(),
				newArticleId: proposal.kind === "CREATE" ? newId() : undefined,
				newArticleUrlName: proposal.kind === "CREATE" ? newId() : undefined,
				discardedHunkIds: discarded.length ? discarded : undefined,
				editedHtml: carriesDraft ? draft?.html : undefined,
				editedTitle: carriesDraft ? draft?.title : undefined,
			},
		}
	})
}

export function useBulkReviewActions({
	proposals,
	articleId,
	discardedByProposal,
	hunkCountByProposal,
}: {
	proposals: SuggestionProposal[]
	articleId: string | null
	discardedByProposal?: Map<string, string[]>
	hunkCountByProposal?: Map<string, number>
}) {
	const zero = useZero()
	const navigate = useNavigate()
	const router = useRouter()
	const workspace = useZeroWorkspace()
	const decodedToken = useAuthData()
	const { visibleRows } = useArticleSuggestions()
	const { setPendingMessage, removePendingMessage } = useUndoContext()
	const [pending, setPending] = useState<"accept" | "reject" | null>(null)
	const busy = pending !== null

	const first = proposals[0]
	const currentKey = articleId ?? (first ? createDraftKey(first.id) : "")
	const draft = useReviewDraftStore((state) => state.drafts[currentKey])
	const clearDraft = useReviewDraftStore((state) => state.clearDraft)

	const goToNextRow = () => {
		const next = nextSuggestionRow(visibleRows, currentKey)
		void navigate({ href: next ? reviewRouteForRow(next) : "/edit-docs" })
	}

	const showUndoToast = (title: string, undo: () => void) => {
		const undoId = generateId()
		setPendingMessage(undoId)
		toast.undo({
			title,
			data: { messageId: undoId },
			layout: "inline",
			action: {
				label: "Undo",
				callback: () => {
					removePendingMessage(undoId)
					undo()
					router.history.back()
				},
				shortcut: <Shortcut>Z</Shortcut>,
			},
		})
	}

	const acceptAll = async () => {
		if (busy || proposals.length === 0) return
		setPending("accept")
		const steps = planBulkAccept({
			proposals,
			discardedByProposal,
			hunkCountByProposal,
			draft,
			newId: generateId,
		})
		const done: BulkStep[] = []
		try {
			for (const step of steps) {
				if (step.action === "reject") {
					await mutateOk(
						zero.mutate(
							mutators.helpCenterArticleProposals.reject({
								id: step.proposalId,
							}),
						),
					)
				} else {
					await mutateOk(
						zero.mutate(
							mutators.helpCenterArticleProposals.accept({
								id: step.args.proposalId,
								newArticleVersionId: step.args.newArticleVersionId,
								newArticleId: step.args.newArticleId,
								newArticleUrlName: step.args.newArticleUrlName,
								discardedHunkIds: step.args.discardedHunkIds,
								editedHtml: step.args.editedHtml,
								editedTitle: step.args.editedTitle,
							}),
						),
					)
				}
				done.push(step)
			}
			clearDraft(currentKey)
			showUndoToast(acceptedTitle(done.length), () => {
				for (const step of [...done].reverse()) {
					const mutation =
						step.action === "reject"
							? mutators.helpCenterArticleProposals.reopen({
									id: step.proposalId,
								})
							: mutators.helpCenterArticleProposals.unaccept({
									id: step.args.proposalId,
									newArticleVersionId: step.args.newArticleVersionId,
									newArticleId: step.args.newArticleId,
								})
					void zero.mutate(mutation).client.catch((err: unknown) => {
						reportBulkError(err, "undo accept all")
					})
				}
			})
			// The docs tree only holds foldered articles, so a folderless one stays here.
			const [createdArticleId] = done.flatMap((step) =>
				step.action === "accept" &&
				step.args.newArticleId &&
				proposals.find((p) => p.id === step.args.proposalId)?.proposedGroupId
					? [step.args.newArticleId]
					: [],
			)
			if (createdArticleId)
				void navigate({
					to: "/edit-docs/$id",
					params: { id: createdArticleId },
				})
			else goToNextRow()
		} catch (err) {
			reportBulkError(err, "accept all docs drafts")
			toast.error({ title: "Failed to accept the suggestions" })
		} finally {
			setPending(null)
		}
	}

	const rejectAll = async () => {
		if (busy || proposals.length === 0) return
		setPending("reject")
		const done: string[] = []
		try {
			for (const proposal of proposals) {
				await mutateOk(
					zero.mutate(
						mutators.helpCenterArticleProposals.reject({ id: proposal.id }),
					),
				)
				done.push(proposal.id)
			}
			clearDraft(currentKey)
			showUndoToast(rejectedTitle(done.length), () => {
				for (const id of [...done].reverse()) {
					void zero
						.mutate(mutators.helpCenterArticleProposals.reopen({ id }))
						.client.catch((err: unknown) => {
							reportBulkError(err, "undo reject all")
						})
				}
			})
			goToNextRow()
		} catch (err) {
			reportBulkError(err, "reject all docs drafts")
			toast.error({ title: "Failed to reject the suggestions" })
		} finally {
			setPending(null)
		}
	}

	function reportBulkError(err: unknown, action: string) {
		console.error(err)
		captureError(err, {
			domain: "general",
			action,
			workspaceId: workspace?.id,
			userId: decodedToken.userId,
			extra: { proposalIds: proposals.map((p) => p.id) },
		})
	}

	return { acceptAll, rejectAll, pending }
}

function acceptedTitle(count: number): string {
	return count === 1 ? "Suggestion accepted" : `${count} suggestions accepted`
}

function rejectedTitle(count: number): string {
	return count === 1 ? "Suggestion rejected" : `${count} suggestions rejected`
}
